import api from './api';
import { ENDPOINTS, VALID_ORDER_STATUSES } from '../utils/constants';
import { formatApiError, logError } from '../utils/errorHandler';

/**
 * Order service functions for customer and employee order management
 */

// ==================== Customer Order Functions ====================

/**
 * Fetch hosted invoice URL and PDF link from Stripe (customer-facing)
 * @param {number} orderId - Order ID
 * @returns {Promise<{hosted_invoice_url: string|null, invoice_pdf: string|null}>}
 */
export const getCustomerInvoiceLinks = async (orderId) => {
  try {
    const response = await api.get(ENDPOINTS.ORDERS.CUSTOMER.INVOICE_LINKS(orderId));
    return response.data;
  } catch (error) {
    logError('Get customer invoice links', error);
    throw formatApiError(error);
  }
};

/**
 * Get all orders for the authenticated customer's company
 * @returns {Promise<Array>} - Array of orders
 * @throws {Object} - Formatted error object
 */
export const getMyOrders = async () => {
  try {
    const response = await api.get(ENDPOINTS.ORDERS.CUSTOMER.LIST);
    return response.data;
  } catch (error) {
    logError('Get my orders', error);
    throw formatApiError(error);
  }
};

/**
 * Get a single order by ID (customer can only access their company's orders)
 * @param {number} orderId - Order ID
 * @returns {Promise<Object>} - Order object with items
 * @throws {Object} - Formatted error object
 */
export const getOrder = async (orderId) => {
  if (!orderId || typeof orderId !== 'number') {
    throw { message: 'Invalid order ID' };
  }

  try {
    const response = await api.get(ENDPOINTS.ORDERS.CUSTOMER.DETAIL(orderId));
    return response.data;
  } catch (error) {
    logError('Get order', error);
    throw formatApiError(error);
  }
};

/**
 * Create a new order
 * @param {Object} orderData - Order creation data
 * @param {Array} orderData.items - Array of order items with product_variant and quantity
 * @param {string} orderData.delivery_date - Requested delivery date (YYYY-MM-DD)
 * @param {string} orderData.delivery_address - Delivery address
 * @param {string} orderData.special_instructions - Optional special instructions
 * @returns {Promise<Object>} - Created order object
 * @throws {Object} - Formatted error object
 */
export const createOrder = async (orderData) => {
  // Validate order data
  if (!orderData?.items || !Array.isArray(orderData.items) || orderData.items.length === 0) {
    throw { message: 'Order must contain at least one item' };
  }

  try {
    const response = await api.post(ENDPOINTS.ORDERS.CUSTOMER.LIST, orderData);
    return response.data;
  } catch (error) {
    logError('Create order', error);
    throw formatApiError(error);
  }
};

// ==================== Employee Order Functions ====================

/**
 * Get all orders across all companies (employee only)
 * @param {Object} filters - Optional filters
 * @param {number} filters.company - Filter by company ID
 * @param {string} filters.status - Filter by status (pending, confirmed, in_production, etc.)
 * @param {string} filters.search - Search by company name
 * @param {string} filters.dateFrom - Filter orders placed on or after this date (YYYY-MM-DD)
 * @param {string} filters.dateTo - Filter orders placed on or before this date (YYYY-MM-DD)
 * @returns {Promise<Object>} - Paginated orders response
 * @throws {Object} - Formatted error object
 */
export const getAllOrders = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.company)   params.append('company', filters.company);
    if (filters.status)    params.append('status', filters.status);
    if (filters.search)    params.append('search', filters.search);
    if (filters.dateFrom)  params.append('created_at__gte', filters.dateFrom);
    if (filters.dateTo)    params.append('created_at__lte', filters.dateTo);
    if (filters.session)   params.append('session', filters.session);
    if (filters.page_size) params.append('page_size', filters.page_size);

    const queryString = params.toString();
    const url = queryString ? `${ENDPOINTS.ORDERS.EMPLOYEE.LIST}?${queryString}` : ENDPOINTS.ORDERS.EMPLOYEE.LIST;

    const response = await api.get(url);
    return response.data;
  } catch (error) {
    logError('Get all orders', error);
    throw formatApiError(error);
  }
};

/**
 * Create a new order on behalf of a customer (employee only)
 * @param {Object} orderData - Order data including company, items, fulfillment details
 * @returns {Promise<Object>} - Created order object
 * @throws {Object} - Formatted error object
 */
export const createOrderAsEmployee = async (orderData) => {
  if (!orderData?.items || !Array.isArray(orderData.items) || orderData.items.length === 0) {
    throw { message: 'Order must contain at least one item' };
  }

  try {
    const response = await api.post(ENDPOINTS.ORDERS.EMPLOYEE.LIST, orderData);
    return response.data;
  } catch (error) {
    logError('Create order as employee', error);
    throw formatApiError(error);
  }
};

/**
 * Get a single order by ID (employee can access any order)
 * @param {number} orderId - Order ID
 * @returns {Promise<Object>} - Order object with items and company details
 * @throws {Object} - Formatted error object
 */
export const getOrderAsEmployee = async (orderId) => {
  if (!orderId || typeof orderId !== 'number') {
    throw { message: 'Invalid order ID' };
  }

  try {
    const response = await api.get(ENDPOINTS.ORDERS.EMPLOYEE.DETAIL(orderId));
    return response.data;
  } catch (error) {
    logError('Get order as employee', error);
    throw formatApiError(error);
  }
};

/**
 * Get aggregate order statistics for the employee dashboard (employee only)
 * @param {Object} params
 * @param {string} params.dateFrom - Start date YYYY-MM-DD (inclusive)
 * @param {string} params.dateTo   - End date YYYY-MM-DD (inclusive)
 * @param {number|string} params.session - Mill session ID to filter by (optional)
 * @returns {Promise<Object>} - Stats object with totals, by_status, by_month, by_product
 */
export const getOrderStats = async ({ dateFrom, dateTo, session } = {}) => {
  try {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo)   params.append('date_to',   dateTo);
    if (session)  params.append('session',   session);

    const qs  = params.toString();
    const url = qs ? `${ENDPOINTS.ORDERS.EMPLOYEE.STATS}?${qs}` : ENDPOINTS.ORDERS.EMPLOYEE.STATS;

    const response = await api.get(url);
    return response.data;
  } catch (error) {
    logError('Get order stats', error);
    throw formatApiError(error);
  }
};

/**
 * Update order status (employee only)
 * @param {number} orderId - Order ID
 * @param {string} status - New status (pending, confirmed, in_production, ready, delivered, cancelled)
 * @returns {Promise<Object>} - Updated order object
 * @throws {Object} - Formatted error object
 */
export const updateOrderStatus = async (orderId, status) => {
  if (!orderId || typeof orderId !== 'number') {
    throw { message: 'Invalid order ID' };
  }

  if (!status || !VALID_ORDER_STATUSES.includes(status)) {
    throw { message: `Invalid status. Must be one of: ${VALID_ORDER_STATUSES.join(', ')}` };
  }

  try {
    const response = await api.patch(ENDPOINTS.ORDERS.EMPLOYEE.UPDATE_STATUS(orderId), { status });
    // Backend returns { message, order } — unwrap to the order object for convenience
    return response.data?.order ?? response.data;
  } catch (error) {
    logError('Update order status', error);
    throw formatApiError(error);
  }
};

/**
 * Bulk-update status for multiple orders (employee only).
 * @param {number[]} orderIds
 * @param {string} newStatus
 * @returns {{ updated: number, errors: Array }}
 */
export const bulkUpdateOrderStatus = async (orderIds, newStatus) => {
  if (!Array.isArray(orderIds) || orderIds.length === 0) {
    throw { message: 'order_ids must be a non-empty array.' };
  }
  if (!newStatus || !VALID_ORDER_STATUSES.includes(newStatus)) {
    throw { message: `Invalid status. Must be one of: ${VALID_ORDER_STATUSES.join(', ')}` };
  }
  try {
    const response = await api.post(ENDPOINTS.ORDERS.EMPLOYEE.BULK_UPDATE_STATUS, {
      order_ids: orderIds,
      status: newStatus,
    });
    return response.data;
  } catch (error) {
    logError('Bulk update order status', error);
    throw formatApiError(error);
  }
};

/**
 * Merge multiple orders into one consolidated order (employee only).
 * @param {number[]} orderIds - IDs of the orders to merge (>= 2, same company)
 * @returns {Promise<Object>} - The new consolidated order
 * @throws {Error} - Formatted error object
 */
export const mergeOrders = async (orderIds) => {
  if (!Array.isArray(orderIds) || orderIds.length < 2) {
    throw { message: 'Select at least two orders to merge.' };
  }
  try {
    const response = await api.post(ENDPOINTS.ORDERS.EMPLOYEE.MERGE, {
      order_ids: orderIds,
    });
    return response.data;
  } catch (error) {
    logError('Merge orders', error);
    throw formatApiError(error);
  }
};

/**
 * Update order details (employee only) — fields and/or line items.
 * @param {number} orderId - Order ID
 * @param {Object} data - Fields to update (all optional):
 *   fulfillment_type, delivery_date, delivery_address (ID), session (ID),
 *   special_instructions, items [{product_variant_id, quantity}]
 * @returns {Promise<Object>} - Updated order object
 * @throws {Error} - Formatted error object
 */
export const updateOrderDetails = async (orderId, data) => {
  if (!orderId || typeof orderId !== 'number') {
    throw new Error('Invalid order ID');
  }

  try {
    const response = await api.patch(ENDPOINTS.ORDERS.EMPLOYEE.UPDATE_ORDER(orderId), data);
    return response.data?.order ?? response.data;
  } catch (error) {
    logError('Update order details', error);
    throw formatApiError(error);
  }
};

// ==================== Employee Invoice Functions ====================

/**
 * Fetch hosted invoice URL and PDF link from Stripe (employee only)
 * @param {number} orderId - Order ID
 * @returns {Promise<{hosted_invoice_url: string|null, invoice_pdf: string|null}>}
 */
export const getInvoiceLinks = async (orderId) => {
  try {
    const response = await api.get(ENDPOINTS.ORDERS.EMPLOYEE.INVOICE_LINKS(orderId));
    return response.data;
  } catch (error) {
    logError('Get invoice links', error);
    throw formatApiError(error);
  }
};

/**
 * Create a Stripe invoice for an order (employee only)
 * @param {number} orderId - Order ID
 * @returns {Promise<Object>} - Updated order object with stripe_invoice_id
 */
export const setDiscount = async (orderId, discountCents) => {
  try {
    const response = await api.post(ENDPOINTS.ORDERS.EMPLOYEE.SET_DISCOUNT(orderId), { discount_cents: discountCents });
    return response.data;
  } catch (error) {
    logError('Set discount', error);
    throw formatApiError(error);
  }
};

export const createInvoice = async (orderId, applyCredit = true) => {
  try {
    const response = await api.post(ENDPOINTS.ORDERS.EMPLOYEE.CREATE_INVOICE(orderId), { apply_credit: applyCredit });
    return response.data?.order ?? response.data;
  } catch (error) {
    logError('Create invoice', error);
    throw formatApiError(error);
  }
};

/**
 * Finalize and email the Stripe invoice to the customer (employee only)
 * @param {number} orderId - Order ID
 * @returns {Promise<Object>} - Updated order object
 */
export const sendInvoice = async (orderId) => {
  try {
    const response = await api.post(ENDPOINTS.ORDERS.EMPLOYEE.SEND_INVOICE(orderId));
    return response.data?.order ?? response.data;
  } catch (error) {
    logError('Send invoice', error);
    throw formatApiError(error);
  }
};

/**
 * Resend the Stripe invoice email to the customer (employee only)
 * @param {number} orderId - Order ID
 * @returns {Promise<Object>} - Updated order object
 */
export const resendInvoice = async (orderId) => {
  try {
    const response = await api.post(ENDPOINTS.ORDERS.EMPLOYEE.RESEND_INVOICE(orderId));
    return response.data?.order ?? response.data;
  } catch (error) {
    logError('Resend invoice', error);
    throw formatApiError(error);
  }
};

/**
 * Mark a Stripe invoice as paid outside of Stripe (for check payments)
 * @param {number} orderId - Order ID
 * @returns {Promise<Object>} - Updated order object
 */
export const voidInvoice = async (orderId) => {
  try {
    const response = await api.post(ENDPOINTS.ORDERS.EMPLOYEE.VOID_INVOICE(orderId));
    return response.data?.order ?? response.data;
  } catch (error) {
    logError('Void invoice', error);
    throw formatApiError(error);
  }
};

export const markPaidOutsideStripe = async (orderId) => {
  try {
    const response = await api.post(ENDPOINTS.ORDERS.EMPLOYEE.MARK_PAID_OUTSIDE_STRIPE(orderId));
    return response.data?.order ?? response.data;
  } catch (error) {
    logError('Mark paid outside Stripe', error);
    throw formatApiError(error);
  }
};

/**
 * Update order internal notes (employee only)
 * @param {number} orderId - Order ID
 * @param {string} notes - Internal notes text
 * @returns {Promise<Object>} - Updated order object
 * @throws {Object} - Formatted error object
 */
export const updateOrderNotes = async (orderId, notes) => {
  if (!orderId || typeof orderId !== 'number') {
    throw { message: 'Invalid order ID' };
  }

  if (typeof notes !== 'string') {
    throw { message: 'Notes must be a string' };
  }

  try {
    const response = await api.patch(ENDPOINTS.ORDERS.EMPLOYEE.UPDATE_NOTES(orderId), {
      internal_notes: notes,
    });
    // Backend returns { message, order } — unwrap to the order object for convenience
    return response.data?.order ?? response.data;
  } catch (error) {
    logError('Update order notes', error);
    throw formatApiError(error);
  }
};
